import { ErrorMessage } from '@hookform/error-message';
import { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import DmmCardBannerImg from '@/assets/images/banner/bnr_dmmcard.png';
import DmmCardBannerImgWebp from '@/assets/images/banner/bnr_dmmcard.png?webp';
import Banner from '@/components/elements/Banner';
import SkyflagBanner from '@/components/elements/Banner/Skyflag';
import Button from '@/components/elements/Button/BaseButton';
import Input from '@/components/elements/Form/Input';
import Radio from '@/components/elements/Form/Radio';
import {
  PAYMENT_FROM_CREDIT_MAX_VALUE,
  PAYMENT_FROM_CREDIT_MIN_VALUE,
} from '@/config/constants/payment';
import { PAYOUT_TORETA_MIN_VALUE } from '@/config/constants/payout';
import { TOKUSHOU_TERMS_URL } from '@/config/constants/terms';
import { useUserContext } from '@/contexts/user';
import { useDidUpdateEffect } from '@/hooks/useDidUpdateEffect';
import { CreditCardMemberStatus } from '@/lib/apollo/types/gen/graphql';
import {
  getToretaChargeUrl,
  getToretaPinChargeUrl,
  PayType,
} from '@/lib/dmm/point';
import { ChargeType } from '@/modelTypes';
import { creditCardSelector } from '@/selectors/creditCardBrand';

import { DailyOrderLimitLinkArea, ExternalLink } from '../style';
import {
  Acknowledgment,
  AmountWrapper,
  BannerBorder,
  BannerWrapper,
  ContentWrapper,
  CreditDormantLink,
  CreditDormantNote,
  CreditDormantTitle,
  CreditDormantWrapper,
  Error,
  ErrorWrapper,
  ExternalLinkWrapper,
  HowtoLinkWrapper,
  InputWrapper,
  MerpayIcon,
  PaypayIcon,
  RadioWrapper,
  RegisterWrapper,
  SubmitWrapper,
  TopBox,
} from './style';

interface QuickChargeTopProps {
  chargeType: ChargeType;
  creditCardStatus: CreditCardMemberStatus;
  setChargeType: React.Dispatch<React.SetStateAction<ChargeType>>;
  handleClose: () => void;
  chargeToretaFromCredit: (amount: number) => void;
  chargeToretaFromWallet: (amount: number) => void;
}

interface FormData {
  chargeAmount: string;
}

const DmmCardBanner: FC<{
  onClick: QuickChargeTopProps['handleClose'];
}> = ({ onClick }) => {
  return (
    <div onClick={onClick}>
      <BannerBorder>
        <Banner
          srcSet={DmmCardBannerImg}
          mediaSet={[
            {
              srcSet: DmmCardBannerImgWebp,
              type: 'image/webp',
            },
          ]}
          overflow
          alt="toreta+チャージはDMMカードがお得！DMMカードでtoreta+をチャージすると最大1.5%toreta+還元！"
          width="192px"
          height="60px"
          link="https://card.dmm.com/recommend/toreta/"
          external={true}
        />
      </BannerBorder>
    </div>
  );
};

const QuickChargeTop: React.FC<QuickChargeTopProps> = ({
  chargeType,
  creditCardStatus,
  setChargeType,
  handleClose,
  chargeToretaFromCredit,
  chargeToretaFromWallet,
}) => {
  const { user, isMember } = useUserContext();
  const { isAvailable, isPcc, isPccDmm } = creditCardSelector(user.creditCard);
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    trigger,
  } = useForm<FormData>({
    mode: 'onBlur',
  });
  const [isChargeCalled, setIsChargeCalled] = useState<boolean>(false);

  useDidUpdateEffect(() => {
    trigger('chargeAmount');
  }, [chargeType]);

  const onSubmit: SubmitHandler<FormData> = data => {
    setIsChargeCalled(true);

    if (chargeType === ChargeType.Credit) {
      chargeToretaFromCredit(Number(data.chargeAmount));
      return;
    }
    if (chargeType === ChargeType.Wallet) {
      chargeToretaFromWallet(Number(data.chargeAmount));
      return;
    }
  };

  const chargeMethods: {
    type: ChargeType;
    name: string | JSX.Element;
    disabled: boolean;
  }[] = [
    {
      type: ChargeType.Credit,
      name: 'クレジットカード',
      disabled:
        !isPcc ||
        !isAvailable ||
        creditCardStatus === CreditCardMemberStatus.Dormant,
    },
    {
      type: ChargeType.Wallet,
      name: '入金/払戻金 残高',
      disabled: (user.wallet?.balance || 0) <= 0,
    },
  ];

  const chargeAmounts = [1000, 10000, 50000];

  const externalChargeMethods: {
    type: PayType | 'toretaPin';
    name: JSX.Element | string;
  }[] = [
    {
      type: PayType.Paypay,
      name: (
        <>
          <PaypayIcon />
          PayPay
        </>
      ),
    },
    {
      type: PayType.Merpay,
      name: (
        <>
          <MerpayIcon />
          メルペイ
        </>
      ),
    },
    {
      type: 'toretaPin',
      name: 'toreta+ 番号',
    },
    {
      type: PayType.Bank,
      name: '銀行振込',
    },
  ];

  // 入力およびサブミットを不可能にする
  const disabled = chargeMethods.every(cm => cm.disabled);

  const disabledChargeAmount = () => {
    if (chargeMethods.every(cm => cm.disabled)) {
      return true;
    }
    if (
      chargeType === ChargeType.Credit &&
      creditCardStatus === CreditCardMemberStatus.Dormant
    ) {
      return true;
    }
    return false;
  };

  return (
    <ContentWrapper>
      <TopBox>
        <RadioWrapper>
          {chargeMethods.map(c => (
            <Radio
              key={c.type}
              htmlFor={`charge-${c.type}`}
              checked={chargeType === c.type && !c.disabled}
              name="chargeType"
              disabled={c.disabled}
              onChange={() => setChargeType(c.type)}
            >
              {c.name}
            </Radio>
          ))}
        </RadioWrapper>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputWrapper>
            <Input
              id="chargeAmount"
              type="tel"
              placeholder="0"
              autoComplete="off"
              bgColor="lightGray"
              width="100%"
              height="48px"
              textAlign="right"
              fontSize="m"
              disabled={disabledChargeAmount()}
              hasError={Boolean(errors.chargeAmount)}
              {...register('chargeAmount', {
                required: '金額を指定してください。',
                pattern: {
                  value: /^[0-9]+$/,
                  message: '半角数字で入力してください。',
                },
                validate: {
                  validMin: val => {
                    if (
                      chargeType === ChargeType.Credit &&
                      Number(val) < PAYMENT_FROM_CREDIT_MIN_VALUE
                    ) {
                      return `${PAYMENT_FROM_CREDIT_MIN_VALUE.toLocaleString()}円以上で入力してください。`;
                    }
                    if (
                      chargeType === ChargeType.Wallet &&
                      Number(val) < PAYOUT_TORETA_MIN_VALUE
                    ) {
                      return `${PAYOUT_TORETA_MIN_VALUE.toLocaleString()}円以上で入力してください。`;
                    }
                    return true;
                  },
                  validMax: val => {
                    if (
                      chargeType === ChargeType.Credit &&
                      Number(val) > PAYMENT_FROM_CREDIT_MAX_VALUE
                    ) {
                      return `${PAYMENT_FROM_CREDIT_MAX_VALUE.toLocaleString()}円以下で入力してください。`;
                    }
                    return true;
                  },
                  InsufficientBalance: val => {
                    if (
                      chargeType === ChargeType.Wallet &&
                      Number(val) > (user.wallet?.balance || 0)
                    ) {
                      return '入金/払戻金 残高以下で入力してください。';
                    }
                    return true;
                  },
                },
              })}
            />
            <span>円分</span>
            <ErrorWrapper>
              <ErrorMessage
                errors={errors}
                name="chargeAmount"
                render={({ message }) => <Error>{message}</Error>}
              />
            </ErrorWrapper>
          </InputWrapper>
          <AmountWrapper>
            {chargeAmounts.map(amount => (
              <Button
                key={`charge-${amount}`}
                type="button"
                bgColor="secondary"
                size="s"
                height="40px"
                radius="m"
                fontWeight="bold"
                outline
                disabled={disabledChargeAmount()}
                onClick={() => {
                  setValue(
                    'chargeAmount',
                    `${Number(getValues('chargeAmount') || 0) + amount}`,
                  );
                  clearErrors('chargeAmount');
                }}
              >
                +{amount.toLocaleString()}
              </Button>
            ))}
          </AmountWrapper>
          <Acknowledgment>
            toreta+ は決済完了後即時に利用できます。
            <br />
            toreta+
            はいかなる場合も返金または換金することはできません。予めご了承ください。
          </Acknowledgment>
          {!isPcc && (
            <DailyOrderLimitLinkArea style={{ marginTop: '16px' }}>
              <ExternalLink
                href={`https://card.dmm.com/recommend/toreta/`}
                target="_blank"
              >
                DMM
                JCBカードはご利用額に応じて最大1.5％のtoreta+を受け取ることができます。
                <br />
                まだご利用でない方はご検討ください。
              </ExternalLink>
            </DailyOrderLimitLinkArea>
          )}
          {isAvailable &&
          chargeType === ChargeType.Credit &&
          creditCardStatus === CreditCardMemberStatus.Dormant ? (
            <CreditDormantWrapper>
              <CreditDormantTitle>
                ただいまクレジットカードを利用することができません
              </CreditDormantTitle>
              <CreditDormantNote>
                以下リンクより、登録しているクレジットカード情報をご確認ください。
              </CreditDormantNote>
              <CreditDormantLink>
                <a href={import.meta.env.VITE_DMM_PAYMENT_URL}>
                  クレジットカード情報を確認する
                </a>
              </CreditDormantLink>
            </CreditDormantWrapper>
          ) : (
            <SubmitWrapper>
              <Button
                bgColor="primary"
                size="m"
                radius="l"
                height="56px"
                fontWeight="bold"
                isLoading={isChargeCalled}
                disabled={
                  Boolean(errors.chargeAmount) || isChargeCalled || disabled
                }
              >
                チャージする
              </Button>
            </SubmitWrapper>
          )}
        </form>
        {!isAvailable && (
          <RegisterWrapper>
            {user.creditCard && (
              <p>
                お客様がご登録のクレジットカードはtoreta+
                チャージにご利用できません。
                <br />
                下記より登録クレジットカードを変更してからチャージしてください。
              </p>
            )}
            <a href={import.meta.env.VITE_DMM_PAYMENT_URL} target="_blank">
              クレジットカードの登録はこちら
            </a>
            <p>
              利用可能なクレジットカードは
              <a
                href="https://support.dmm.com/payment/article/46418"
                target="_blank"
              >
                こちら
              </a>
              から確認してください。
            </p>
          </RegisterWrapper>
        )}
        <BannerWrapper>
          {!isAvailable && !isPccDmm && <DmmCardBanner onClick={handleClose} />}
          {isMember && <SkyflagBanner width={192} height={60} overflow />}
        </BannerWrapper>
      </TopBox>
      <ExternalLinkWrapper>
        {externalChargeMethods.map(ec => (
          <a
            key={ec.type}
            href={
              ec.type === 'toretaPin'
                ? getToretaPinChargeUrl()
                : getToretaChargeUrl({ payType: ec.type as PayType })
            }
          >
            <Button
              bgColor="secondary"
              size="s"
              height="48px"
              radius="m"
              fontWeight="bold"
              outline
            >
              {ec.name}
            </Button>
          </a>
        ))}
      </ExternalLinkWrapper>
      <HowtoLinkWrapper>
        <a href={`${TOKUSHOU_TERMS_URL}#payment_method`} target="_blank">
          toreta+ の支払い方法について
        </a>
      </HowtoLinkWrapper>
      {isAvailable && !isPccDmm && (
        <BannerWrapper>
          <DmmCardBanner onClick={handleClose} />
        </BannerWrapper>
      )}
    </ContentWrapper>
  );
};

export default QuickChargeTop;
